// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchFundInvestmentDetailFiltered,
  fetchFundInvestmentDetailFilterOptions,
  type FundInvestmentDetailPaginatedResponse
} from '../services/fundsService.ts'
import {
  FUND_INVESTMENT_DEFAULT_AGGREGATION,
  resolveDimLabelColumn
} from '../components/aggregations/aggregationDimensions.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  areStringArraysEqual,
  getSubPreferenceRow,
  PREF_SUBKEY_FUND_INVESTMENT_DETAIL,
  PREF_TABLE_AGGREGATION,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableAggregationFromPreferenceRow,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow,
  type FilterValue,
  type SortConfig
} from '../../global_configs/tableViewPreferences.ts'
import { MENU_ITEM_KEY_FUNDS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import type { AppDispatch } from '../../../state_manager/store.ts'

export const FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION: string[] = FUND_INVESTMENT_DEFAULT_AGGREGATION
const DEFAULT_FILTERS: FilterValue[] = []

/**
 * Derive an auto-sort from an aggregation list. For each dim:
 *   1. Look up its label-column `field_name` via `DIM_LABEL_FIELD`.
 *   2. Find the corresponding row in Redux `globalConfigs.reporting_column_defaults`.
 *   3. Use its `field_name` as the sort key, direction asc.
 * The wire form is `field_name` (matching every other grid). The Funds-family
 * router translates `field_name → cfg.sort_key` before invoking the RPC, so
 * both the frontend Sort drawer (which renders options by `field_name`) and
 * the RPC's ORDER BY (which keys off `sort_key`) see consistent identifiers.
 */
function deriveSortFromAggregation(aggregation: string[]): SortConfig[] {
  const cfg = store.getState().globalConfigs ?? {}
  const reportingColumnDefaults: any[] = (cfg.reporting_column_defaults ?? []) as any[]
  const modules: any[] = (cfg.modules ?? []) as any[]
  const out: SortConfig[] = []
  for (const dim of aggregation) {
    const col = resolveDimLabelColumn(dim, reportingColumnDefaults, modules)
    if (!col) continue
    const fieldName = typeof col.field_name === 'string' ? col.field_name : ''
    if (!fieldName) continue
    if (!out.some((s) => s.key === fieldName)) {
      out.push({ key: fieldName, direction: 'asc' })
    }
  }
  return out
}

export interface FundInvestmentDetailOptions {
  /** ISO timestamp for as-of measurement; null = use server's now() (i.e. live). */
  datetime: string | null
  includeWarehoused: boolean
  includeConvertedOut: boolean
}

export const FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS: FundInvestmentDetailOptions = {
  datetime: null,
  includeWarehoused: false,
  includeConvertedOut: false
}

/**
 * useFundInvestmentDetail — local-state hook for the configurable per-fund
 * Investment Detail table on the Fund Investments tab. Like
 * useFundInvestmentSummary but adds an `activeAggregation` (text[]) that
 * drives the at__get_measures aggregation. Persists alongside filters/sorting
 * under PREF_SUBKEY_FUND_INVESTMENT_DETAIL on the funds menu_item prefs row.
 */
export function useFundInvestmentDetail(fundId: string | null | undefined) {
  const dispatch = useDispatch<AppDispatch>()
  const recordIdString = fundId ? String(fundId) : null

  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_FUNDS
  )

  const [data, setData] = useState<Record<string, unknown>[]>([])
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [activeAggregation, setActiveAggregationLocal] = useState<string[]>(
    FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION
  )
  const [activeFilters, setActiveFiltersLocal] = useState<FilterValue[]>(DEFAULT_FILTERS)
  const [activeSorting, setActiveSortingLocal] = useState<SortConfig[]>(() =>
    deriveSortFromAggregation(FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
  )
  // Session-only — deliberately not persisted. People forget to reset these,
  // so a fresh session always starts from the canonical defaults. We hold
  // both the applied value (drives the fetch) and a draft (drives the panel
  // controls) so the user explicitly clicks Apply to trigger a refetch —
  // matching the Filters apply behaviour.
  const [activeOptions, setActiveOptionsLocal] = useState<FundInvestmentDetailOptions>(
    FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS
  )
  const [draftOptions, setDraftOptions] = useState<FundInvestmentDetailOptions>(
    FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS
  )
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  // 1. Hydrate aggregation/filters/sorting from prefs (under sub-key).
  //    Sort fallback: when no sort is persisted, derive from the (possibly
  //    persisted) aggregation. Manual sorts the user saved are respected;
  //    only the absent-sort case auto-derives.
  useEffect(() => {
    const sub = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTMENT_DETAIL)
    const nextAggregation =
      resolveTableAggregationFromPreferenceRow(sub, FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION) ??
      FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION
    const nextFilters = resolveTableFiltersFromPreferenceRow(sub, DEFAULT_FILTERS)
    const nextSorting = resolveTableSortingFromPreferenceRow(
      sub,
      deriveSortFromAggregation(nextAggregation)
    )
    if (!areStringArraysEqual(nextAggregation, activeAggregation)) setActiveAggregationLocal(nextAggregation)
    if (!areFilterValuesEqual(nextFilters, activeFilters)) setActiveFiltersLocal(nextFilters)
    if (!areSortConfigsEqual(nextSorting, activeSorting)) setActiveSortingLocal(nextSorting)
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceRow])

  // 2. Fetch filter options once we have a fund id.
  useEffect(() => {
    if (!recordIdString) {
      setFilterOptions({})
      return
    }
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundInvestmentDetailFilterOptions(recordIdString, token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordIdString])

  // 3. Fetch data on aggregation/filter/sort/option/fundId change (after prefs ready).
  const fetchPage1 = useCallback(
    async (
      aggregation: string[],
      sorting: SortConfig[],
      filters: FilterValue[],
      opts: FundInvestmentDetailOptions
    ) => {
      if (!recordIdString) return
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundInvestmentDetailPaginatedResponse = await fetchFundInvestmentDetailFiltered(
          recordIdString,
          aggregation,
          sorting,
          filters,
          1,
          null,
          token,
          {
            datetime: opts.datetime,
            includeWarehoused: opts.includeWarehoused,
            includeConvertedOut: opts.includeConvertedOut
          }
        )
        if (res.error) {
          setError(res.error)
          setData([])
          setHasMore(false)
        } else {
          setData(res.data)
          setHasMore(res.hasMore)
          setCurrentPage(1)
        }
      } finally {
        setLoading(false)
      }
    },
    [recordIdString]
  )

  useEffect(() => {
    if (!prefsReady || !recordIdString) return
    void fetchPage1(activeAggregation, activeSorting, activeFilters, activeOptions)
  }, [prefsReady, recordIdString, activeAggregation, activeFilters, activeSorting, activeOptions, fetchPage1])

  // 4. Persist changes to user prefs (under the sub-key).
  const persistMergedSubPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const baseSubData = (baseData[PREF_SUBKEY_FUND_INVESTMENT_DETAIL] as Record<string, unknown>) || {}
      const newSubData: Record<string, unknown> = {
        ...baseSubData,
        ...extra,
        [PREF_TABLE_AGGREGATION]: activeAggregation,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const merged: Record<string, unknown> = {
        ...baseData,
        [PREF_SUBKEY_FUND_INVESTMENT_DETAIL]: newSubData
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeAggregation, activeFilters, activeSorting, persistPreference, dispatch]
  )

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedSubPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist fund investment detail preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeAggregation, activeFilters, activeSorting, persistMergedSubPreference])

  // 5. Public handlers.
  const handleSetAggregation = useCallback(
    (aggregation: string[]) => {
      const cleaned = Array.isArray(aggregation) ? aggregation.filter((a) => typeof a === 'string' && a) : []
      const next = cleaned.length === 0 ? FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION : cleaned
      if (areStringArraysEqual(next, activeAggregation)) return
      setActiveAggregationLocal(next)
      // Auto-update sort to match the new aggregation order. The user can
      // manually override sort afterwards (until they change aggregation again).
      setActiveSortingLocal(deriveSortFromAggregation(next))
    },
    [activeAggregation]
  )

  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      setActiveFiltersLocal(filters)
    },
    [activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      setActiveSortingLocal(sorting)
    },
    [activeSorting]
  )

  const handleClearFilters = useCallback(() => {
    setActiveFiltersLocal([])
  }, [])

  const handleClearSorting = useCallback(() => {
    setActiveSortingLocal(deriveSortFromAggregation(activeAggregation))
  }, [activeAggregation])

  const handleResetAll = useCallback(() => {
    const defaultSort = deriveSortFromAggregation(FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
    if (!menuItemId) {
      setActiveAggregationLocal(FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
      setActiveFiltersLocal(DEFAULT_FILTERS)
      setActiveSortingLocal(defaultSort)
      return
    }
    void (async () => {
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const merged = { ...baseData }
      delete (merged as any)[PREF_SUBKEY_FUND_INVESTMENT_DETAIL]
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        skipPersistRef.current = true
        setActiveAggregationLocal(FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION)
        setActiveFiltersLocal(DEFAULT_FILTERS)
        setActiveSortingLocal(defaultSort)
      }
    })()
  }, [menuItemId, persistPreference, dispatch])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedSubPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedSubPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const baseData = (row?.data ?? {}) as Record<string, unknown>
    const baseSubData = ((baseData[PREF_SUBKEY_FUND_INVESTMENT_DETAIL] as Record<string, unknown>) || {})
    const newSubData = { ...baseSubData }
    delete (newSubData as any)[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...baseData,
      [PREF_SUBKEY_FUND_INVESTMENT_DETAIL]: {
        ...newSubData,
        [PREF_TABLE_AGGREGATION]: activeAggregation,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      dispatch(mergeUserPreferenceRowData(menuItemId, merged))
    }
    return result
  }, [menuItemId, activeAggregation, activeFilters, activeSorting, persistPreference, dispatch])

  const refetch = useCallback(
    () => fetchPage1(activeAggregation, activeSorting, activeFilters, activeOptions),
    [fetchPage1, activeAggregation, activeSorting, activeFilters, activeOptions]
  )

  const handleSetDraftOptions = useCallback((opts: FundInvestmentDetailOptions) => {
    setDraftOptions(opts)
  }, [])

  const handleApplyOptions = useCallback(() => {
    setActiveOptionsLocal(draftOptions)
  }, [draftOptions])

  const handleCancelOptions = useCallback(() => {
    setDraftOptions(activeOptions)
  }, [activeOptions])

  const handleResetOptionsToDefault = useCallback(() => {
    setDraftOptions(FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS)
    setActiveOptionsLocal(FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS)
  }, [])

  const subPreferenceRow = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_INVESTMENT_DETAIL)

  return {
    data,
    loading,
    error,
    filterOptions,
    filterOptionsLoading,
    activeAggregation,
    activeFilters,
    activeSorting,
    activeOptions,
    draftOptions,
    hasMore,
    currentPage,
    preferenceRow: subPreferenceRow,
    handleSetAggregation,
    handleSetFilters,
    handleSetSorting,
    handleSetDraftOptions,
    handleApplyOptions,
    handleCancelOptions,
    handleResetOptionsToDefault,
    handleClearFilters,
    handleClearSorting,
    handleResetAll,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableAggregation: FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION,
    defaultTableFilters: DEFAULT_FILTERS,
    defaultTableSorting: deriveSortFromAggregation(FUND_INVESTMENT_DETAIL_DEFAULT_AGGREGATION),
    defaultTableOptions: FUND_INVESTMENT_DETAIL_DEFAULT_OPTIONS
  }
}
